import { getCachedAccessToken, getGatewaySession } from '@/lib/gateway-fetch';

export class IntegrationPresignError extends Error {
  readonly statusCode: number | null;

  constructor(message: string, statusCode: number | null) {
    super(message);
    this.name = 'IntegrationPresignError';
    this.statusCode = statusCode;
  }
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Ask edge-lambda-service for a presigned URL for a `/presigned/...` flag path.
 *
 * The same-origin route asks edge-lambda for a `signedUrl` and returns it as
 * `s3_url`. Object keys use `/download`; `/presigned/sockpuppet-upload-url`
 * uses `/sockpuppet-upload-url`.
 */
export async function requestIntegrationPresignedUrl(
  path: string,
  signal?: AbortSignal,
): Promise<string> {
  const cached = getCachedAccessToken();
  const accessToken = cached ?? (await getGatewaySession())?.accessToken ?? null;
  if (!accessToken) {
    throw new IntegrationPresignError('Missing access token', 401);
  }

  let response: Response;
  try {
    response = await fetch('/api/integrations/presigned-url', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ path }),
    });
  } catch (error) {
    if (isAbort(error)) throw error;
    throw new IntegrationPresignError('Presign request failed', null);
  }

  const data = (await response.json().catch(() => null)) as { s3_url?: unknown } | null;
  const s3Url = typeof data?.s3_url === 'string' ? data.s3_url.trim() : '';
  if (!response.ok || !/^https:\/\//i.test(s3Url)) {
    throw new IntegrationPresignError('Presign request failed', response.ok ? null : response.status);
  }
  return s3Url;
}
